import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TrackingMap from "@/components/TrackingMap";
import { Loader2, MapPin, LogIn, LogOut, Wifi, AlertTriangle, Ban, Maximize2, X } from "lucide-react";
import { formatTimeSec, tzForCountry, tzShort } from "@/lib/timezone";

export default function AdminTracking() {
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [userId, setUserId] = useState("");  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  // Set of chronological indices (as strings) the user has hidden from the map
  const [hidden, setHidden] = useState(new Set());
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    (async () => {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/countries")]);
      u.data.sort((a, b) => (a.employee_id || "").localeCompare(b.employee_id || ""));
      setUsers(u.data);
      setCountries(c.data);
    })();
  }, []);

  const filteredUsers = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter(
      (u) =>
        (u.employee_id || "").toLowerCase().includes(s) ||
        (u.name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  const load = async () => {
    if (!userId || !date) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/tracking/user/${userId}`, { params: { date } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load tracking data");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, date]);

  // Reset visibility whenever we load a new day / employee.
  useEffect(() => { setHidden(new Set()); }, [userId, date]);

  // Chronological sequence numbers stay stable regardless of visibility.
  const numberedPoints = useMemo(() => {
    if (!data?.points) return [];
    return data.points.map((p, i) => ({ ...p, _seq: i + 1 }));
  }, [data]);

  const visiblePoints = useMemo(
    () => numberedPoints.filter((p) => !hidden.has(String(p._seq))),
    [numberedPoints, hidden]
  );

  const togglePoint = (seq) => {
    const key = String(seq);
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const showAll = () => setHidden(new Set());
  const hideAll = () => setHidden(new Set(numberedPoints.map((p) => String(p._seq))));

  // Timezone for the target user's country. `data.attendance.country_id` is
  // most authoritative for the day, but for users with no attendance yet we
  // fall back to their profile country.
  const activeTz = useMemo(() => {
    const cid = data?.attendance?.country_id
      || users.find((u) => u.id === userId)?.country_id
      || "";
    return tzForCountry(countries, cid);
  }, [data, users, userId, countries]);
  const tzLabel = useMemo(() => tzShort(activeTz), [activeTz]);

  return (
    <>
      <PageHeader
        eyebrow="Investigation"
        title="Location Map"
        subtitle={`Every GPS ping — punch-in, punch-out, heartbeats · times shown in ${tzLabel}${activeTz ? " (country local)" : " (your local)"}`}
      />
      <div className="p-8 space-y-4">
        {/* ---------- Filters ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="label-mono">Search user</Label>
            <Input
              data-testid="tracking-search"
              placeholder="Employee ID, name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none"
            />
          </div>
          <div>
            <Label className="label-mono">Employee</Label>
            <Select value={userId || undefined} onValueChange={setUserId}>
              <SelectTrigger data-testid="tracking-user-select" className="rounded-none">
                <SelectValue placeholder="Pick employee" />
              </SelectTrigger>
              <SelectContent
                className="rounded-none bg-card max-h-72 z-[1200]"
                position="popper"
                sideOffset={4}
              >
                {filteredUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    <span className="font-mono-brand mr-2">{u.employee_id}</span>
                    {u.name}
                  </SelectItem>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="p-2 text-xs text-muted-foreground">No users match</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono">Date</Label>
            <Input
              data-testid="tracking-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="rounded-none"
            />
          </div>
        </div>

        {/* ---------- Summary + Legend ---------- */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <StatTile label="Total pings" value={data.summary.total_points} />
            <StatTile label="Inside geofence" value={data.summary.inside} tone="ok" />
            <StatTile label="Outside geofence" value={data.summary.outside} tone={data.summary.outside ? "warn" : "muted"} />
            <StatTile label="Heartbeats" value={data.summary.heartbeats} />
            <StatTile label="Rejected punches" value={data.summary.rejected || 0} tone={data.summary.rejected ? "err" : "muted"} />
            <StatTile
              label="Status"
              value={data.attendance?.status || "No record"}
              tone={data.attendance ? (data.attendance.status === "Late" ? "warn" : "ok") : "muted"}
            />
          </div>
        )}
        <Card className="rounded-none border-border p-3 flex flex-wrap gap-3 items-center text-xs">
          <span className="label-mono">Legend</span>
          <LegendPin color="#34d399" label="IN · Punch-in" />
          <LegendPin color="#f472b6" label="OUT · Punch-out" />
          <LegendPin color="#60a5fa" label="• Heartbeat (inside)" />
          <LegendPin color="#fb923c" label="! Heartbeat (OUTSIDE)" />
          <LegendPin color="#ef4444" label="✕ Rejected punch (outside)" />
          <span className="ml-auto text-muted-foreground">
            Toggle map · terrain · satellite from the layers control in the top-right of the map ↗
          </span>
        </Card>

        {/* ---------- Map + Timeline ---------- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 relative">
            {loading && (
              <div className="absolute inset-0 z-10 bg-background/60 grid place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {userId && (
              <Button
                data-testid="fullscreen-map-btn"
                onClick={() => setFullscreen(true)}
                variant="outline"
                size="sm"
                className="absolute top-3 left-3 z-[500] rounded-none border-border bg-background/90 backdrop-blur"
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1.5" /> Full-screen
              </Button>
            )}
            <TrackingMap
              points={visiblePoints}
              centers={data?.centers || []}
              tz={activeTz}
              fitKey={userId && date ? `${userId}|${date}` : null}
            />
          </div>

          <Card className="rounded-none border-border p-4 max-h-[560px] overflow-auto">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="label-mono">
                Timeline{" "}
                {data ? `· ${numberedPoints.length} point${numberedPoints.length === 1 ? "" : "s"}` : ""}
              </div>
              {numberedPoints.length > 0 && (
                <div className="flex gap-1">
                  <button
                    data-testid="timeline-show-all"
                    onClick={showAll}
                    className="text-[10px] label-mono px-2 py-1 border border-border hover:bg-secondary"
                    disabled={hidden.size === 0}
                  >
                    Show all
                  </button>
                  <button
                    data-testid="timeline-hide-all"
                    onClick={hideAll}
                    className="text-[10px] label-mono px-2 py-1 border border-border hover:bg-secondary"
                    disabled={hidden.size === numberedPoints.length}
                  >
                    Hide all
                  </button>
                </div>
              )}
            </div>
            {!userId && (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Pick an employee to see their locations.
              </div>
            )}
            {userId && data && numberedPoints.length === 0 && !loading && (
              <div className="text-sm text-muted-foreground py-8 text-center">
                No GPS activity on this date.
              </div>
            )}
            {error && <div className="text-sm text-red-300 py-4">{error}</div>}
            {data && numberedPoints.length > 0 && (
              <ol className="space-y-2">
                {numberedPoints.map((p) => (
                  <TimelineRow
                    key={p._seq}
                    p={p}
                    tz={activeTz}
                    isHidden={hidden.has(String(p._seq))}
                    onToggle={() => togglePoint(p._seq)}
                  />
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      {/* ---------- FULL-SCREEN MAP DIALOG ---------- */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent
          className="rounded-none bg-background border-border p-0 max-w-[100vw] w-screen h-screen sm:max-w-[100vw] z-[1400] left-0 top-0 translate-x-0 translate-y-0 [&>button]:hidden"
          style={{ maxHeight: "100vh" }}
        >
          <DialogTitle className="sr-only">Location Map · Full-screen</DialogTitle>
          <DialogDescription className="sr-only">
            Interactive map with the selected user's punch, heartbeat and rejected-punch points. Use the timeline sidebar to toggle individual points.
          </DialogDescription>
          <div className="grid grid-cols-1 lg:grid-cols-4 h-screen">
            {/* Map fills 3 columns */}
            <div className="lg:col-span-3 relative border-r border-border">
              <div className="absolute top-3 left-3 z-[500] flex gap-2">
                <div className="rounded-none border border-border bg-background/90 backdrop-blur px-3 py-1.5 text-xs font-mono-brand">
                  {data?.user?.name} · {date} · {tzLabel}
                </div>
              </div>
              <Button
                data-testid="fullscreen-close-btn"
                onClick={() => setFullscreen(false)}
                variant="outline"
                size="sm"
                className="absolute top-3 right-16 z-[500] rounded-none border-border bg-background/90 backdrop-blur"
              >
                <X className="h-3.5 w-3.5 mr-1.5" /> Close
              </Button>
              <TrackingMap
                points={visiblePoints}
                centers={data?.centers || []}
                tz={activeTz}
                fitKey={userId && date ? `${userId}|${date}|fs` : null}
                className="h-screen"
              />
            </div>

            {/* Controls + timeline in a scroll column */}
            <aside className="p-4 overflow-y-auto max-h-screen">
              <div className="space-y-3">
                <div>
                  <Label className="label-mono">Employee</Label>
                  <Select value={userId || undefined} onValueChange={setUserId}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue placeholder="Pick employee" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-card max-h-72 z-[1300]" position="popper">
                      {filteredUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          <span className="font-mono-brand mr-2">{u.employee_id}</span>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Date</Label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="rounded-none"
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="label-mono">
                    Timeline · {numberedPoints.length} pt
                  </div>
                  {numberedPoints.length > 0 && (
                    <div className="flex gap-1">
                      <button
                        onClick={showAll}
                        className="text-[10px] label-mono px-2 py-1 border border-border hover:bg-secondary"
                        disabled={hidden.size === 0}
                      >
                        Show all
                      </button>
                      <button
                        onClick={hideAll}
                        className="text-[10px] label-mono px-2 py-1 border border-border hover:bg-secondary"
                        disabled={hidden.size === numberedPoints.length}
                      >
                        Hide all
                      </button>
                    </div>
                  )}
                </div>

                {numberedPoints.length === 0 && (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    Pick an employee to see their locations.
                  </div>
                )}
                {numberedPoints.length > 0 && (
                  <ol className="space-y-2">
                    {numberedPoints.map((p) => (
                      <TimelineRow
                        key={p._seq}
                        p={p}
                        tz={activeTz}
                        isHidden={hidden.has(String(p._seq))}
                        onToggle={() => togglePoint(p._seq)}
                      />
                    ))}
                  </ol>
                )}
              </div>
            </aside>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatTile({ label, value, tone }) {
  const cls =
    tone === "ok" ? "text-emerald-300" :
    tone === "warn" ? "text-orange-300" :
    tone === "err" ? "text-red-300" :
    tone === "muted" ? "text-muted-foreground" : "";
  return (
    <Card className="rounded-none border-border p-3">
      <div className="label-mono">{label}</div>
      <div className={`font-display font-bold text-2xl ${cls}`}>{value}</div>
    </Card>
  );
}

function LegendPin({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-full border border-[#0c1524]"
        style={{ backgroundColor: color, boxShadow: `0 0 0 2px ${color}55` }}
      />
      <span className="font-mono-brand">{label}</span>
    </span>
  );
}

function TimelineRow({ p, tz, isHidden, onToggle }) {
  const t = formatTimeSec(p.timestamp, tz);
  const iconMap = {
    punch_in: <LogIn className="h-3.5 w-3.5 text-emerald-300" strokeWidth={1.75} />,
    punch_out: <LogOut className="h-3.5 w-3.5 text-pink-300" strokeWidth={1.75} />,
    punch_in_rejected: <Ban className="h-3.5 w-3.5 text-red-300" strokeWidth={1.75} />,
    punch_out_rejected: <Ban className="h-3.5 w-3.5 text-red-300" strokeWidth={1.75} />,
    heartbeat: p.inside_geofence
      ? <Wifi className="h-3.5 w-3.5 text-blue-300" strokeWidth={1.75} />
      : <AlertTriangle className="h-3.5 w-3.5 text-orange-300" strokeWidth={1.75} />,
  };
  const title =
    p.type === "punch_in" ? "Punch-in"
    : p.type === "punch_out" ? "Punch-out"
    : p.type === "punch_in_rejected" ? "Punch-in rejected"
    : p.type === "punch_out_rejected" ? "Punch-out rejected"
    : p.inside_geofence ? "Heartbeat" : "Breach";
  const isRejected = p.type === "punch_in_rejected" || p.type === "punch_out_rejected";
  return (
    <li
      className={`border border-border p-2 text-xs transition-opacity ${isHidden ? "opacity-40" : ""}`}
      data-testid={`timeline-row-${p._seq}`}
    >
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!isHidden}
          onChange={onToggle}
          data-testid={`timeline-toggle-${p._seq}`}
          className="h-3.5 w-3.5 accent-[hsl(var(--accent))] cursor-pointer"
          title={isHidden ? "Show on map" : "Hide from map"}
        />
        <span className="font-mono-brand text-[10px] px-1.5 py-0.5 border border-border bg-secondary/40 tabular-nums">
          #{p._seq}
        </span>
        {iconMap[p.type]}
        <span className="font-mono-brand">{t}</span>
        <span className={isHidden ? "line-through" : ""}>{title}</span>
        {isRejected && (
          <span className="ml-auto px-1.5 py-0.5 border border-red-400/40 bg-red-500/20 text-red-200 label-mono text-[9px]">
            REJECTED
          </span>
        )}
        {!isRejected && !p.inside_geofence && p.type === "heartbeat" && (
          <span className="ml-auto px-1.5 py-0.5 border border-orange-400/40 bg-orange-500/20 text-orange-200 label-mono text-[9px]">
            OUTSIDE
          </span>
        )}
      </div>
      <div className="mt-1 text-muted-foreground font-mono-brand pl-6">
        <MapPin className="h-3 w-3 inline mr-1" />
        {p.latitude?.toFixed(5)}, {p.longitude?.toFixed(5)}
        {p.distance_meters != null && ` · ${p.distance_meters}m from center`}
      </div>
    </li>
  );
}
